import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';

const ContactPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    comment: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Redirect to WhatsApp with form data
    const message = `Hello, my name is ${formData.name}. ${formData.comment}`;
    window.open(`https://wa.me/971585862103?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section 
        className="relative h-[400px] bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1920&q=80)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl">
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">{t('contactUs')}</h1>
            <p className="text-xl text-white/90">
              {t('contactPageSubtitle')}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-12">
          
          {/* Contact Form */}
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('contactFormTitle')}</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contactName')} *
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-logo-blue focus:border-transparent"
                  placeholder={t('contactNamePlaceholder')}
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contactPhone')} *
                </label>
                <input
                  type="tel"
                  id="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-logo-blue focus:border-transparent"
                  placeholder={t('contactPhonePlaceholder')}
                />
              </div>
              
              <div>
                <label htmlFor="comment" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contactComment')} *
                </label>
                <textarea
                  id="comment"
                  required
                  rows="6"
                  value={formData.comment}
                  onChange={(e) => setFormData({...formData, comment: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-logo-blue focus:border-transparent"
                  placeholder={t('contactCommentPlaceholder')}
                ></textarea>
              </div>
              
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-logo-blue to-logo-cyan text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all"
              >
                {t('contactSendBtn')}
              </button>
            </form>
          </div>

          {/* Contact Details */}
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('contactDetailsTitle')}</h2>
            
            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <MapPin className="w-6 h-6 text-logo-blue" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{t('contactCompanyName')}</h3>
                  <p className="text-gray-600 mb-1">{t('contactAddress1')}</p>
                  <p className="text-gray-600 mb-1">{t('contactAddress2')}</p>
                  <p className="text-gray-600 mb-3">{t('contactAddress3')}</p>
                  <div className="flex gap-4">
                    <a
                      href="https://maps.app.goo.gl/c54KgCci71zwrqaS6"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-logo-blue hover:underline font-medium"
                    >
                      {t('contactGoogleMaps')}
                    </a>
                    <a
                      href="https://ul.waze.com/ul?ll=25.06218878%2C55.13859987&navigate=yes&zoom=17"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-logo-blue hover:underline font-medium"
                    >
                      {t('contactWaze')}
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <Phone className="w-6 h-6 text-logo-blue" />
                </div>
                <div>
                  <a href="tel:+971585862103" className="text-gray-900 hover:text-logo-blue font-semibold transition-colors text-lg">
                    +971 58 586 2103
                  </a>
                  <div className="mt-2">
                    <a
                      href="https://wa.me/971585862103"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      {t('contactWhatsApp')}
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <Mail className="w-6 h-6 text-logo-blue" />
                </div>
                <div>
                  <a href="mailto:info@thevisagram.ae" className="text-gray-900 hover:text-logo-blue font-semibold transition-colors text-lg">
                    info@thevisagram.ae
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <Clock className="w-6 h-6 text-logo-blue" />
                </div>
                <div>
                  <p className="text-gray-900 font-semibold">{t('contactWorkingHours')}</p>
                  <p className="text-gray-600">{t('contactWorkingHoursWeek')}</p>
                  <p className="text-gray-600">{t('contactWorkingHoursWeekend')}</p>
                </div>
              </div>
            </div>

            <div className="mt-12 p-6 bg-blue-50 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-3">{t('contactHowToFindTitle')}</h3>
              <p className="text-gray-700">
                {t('contactHowToFindText')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactPage;
